import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ADMIN_API_PREFIX, API_PREFIX, BASE_URL } from '@CONSTANTS/API.constant';
import userRepository from '@REPOSITORIES/local-repository/user-repository';
import { Property, CreatePropertyParams, UpdatePropertyParams } from '@INTERFACES/api/properties';

// Read API (uses API_PREFIX for read operations)
const propertiesReadApiInternal = createApi({
    reducerPath: 'properties-read-api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL + API_PREFIX,
        prepareHeaders: (headers) => {
            const token = userRepository.getData();

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    tagTypes: ['Property'],
    endpoints: (builder) => ({
        getAll: builder.query<Property[], void>({
            query: () => '/properties/',
            providesTags: ['Property'],
        }),
        getById: builder.query<Property, number>({
            query: (id) => `/properties/${id}/`,
            providesTags: (result, error, id) => [{ type: 'Property', id }],
        }),
    }),
});

// Write API (uses ADMIN_API_PREFIX for write operations)
const propertiesWriteApi = createApi({
    reducerPath: 'properties-write-api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL + ADMIN_API_PREFIX,
        prepareHeaders: (headers) => {
            const token = userRepository.getData();

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    tagTypes: ['Property'],
    endpoints: (builder) => ({
        create: builder.mutation<Property, CreatePropertyParams>({
            query: (params) => ({
                url: '/properties/',
                method: 'POST',
                body: params,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: ['Property'],
        }),
        update: builder.mutation<Property, UpdatePropertyParams>({
            query: ({ id, ...params }) => ({
                url: `/properties/${id}/`,
                method: 'PUT',
                body: params,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Property', id }],
        }),
        partialUpdate: builder.mutation<Property, UpdatePropertyParams>({
            query: ({ id, ...params }) => ({
                url: `/properties/${id}/`,
                method: 'PATCH',
                body: params,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Property', id }],
        }),
        delete: builder.mutation<void, number>({
            query: (id) => ({
                url: `/properties/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Property'],
        }),
    }),
});

// Export both APIs for middleware and reducer registration
export const propertiesAdminApi = propertiesWriteApi;
export const propertiesReadApi = propertiesReadApiInternal;

// Export hooks from both APIs
export const {
    useGetAllQuery: useGetAllPropertiesAdmin,
    
} = propertiesReadApiInternal;

export const {
    useCreateMutation: useCreateProperty,
    useUpdateMutation: useUpdateProperty,
    
    useDeleteMutation: useDeleteProperty,
} = propertiesWriteApi;